/**
 * @fileoverview Builder → canonical form adapter.
 * @module inspection-renderer/adapters
 *
 * Responsibilities:
 * - Convert a builder-side `TemplateBuilderForm`-shaped object into the runtime-canonical
 *   `InspectionForm` consumed by the rules engine and the renderer.
 * - Drop builder-only metadata (status / sharing / library / AI / audit) per
 *   the D-0.4 adapter loss matrix.
 *
 * Dependencies:
 * - `@firesquirrel/core-domain` — `InspectionForm`, `InspectionFormField`,
 *   etc. Pure types only. No runtime dependency on the builder package so the
 *   adapter stays runtime-agnostic.
 *
 * Architecture:
 * - Pure synchronous function. No I/O, no host-API calls. Safe to call
 *   inside `useMemo` in a React consumer.
 * - Optional mobile input normalization lives here so host applications
 *   share the canonical field and layout conversion.
 * - The input type is intentionally **structural**: any object that carries
 *   `_id`, `fields`, and `layout` in the canonical shapes is accepted. We do
 *   not import the builder's `TemplateBuilderForm` interface here because the renderer
 *   package must remain host-agnostic (no cross-app imports per ESLint).
 *
 * Notes:
 * - The dropped-fields list is enumerated in
 *   `Docs/inspection-renderer-phase0-audit/03-adapter-loss-matrix.md` §A.
 * - `bindingMode` and `bindingHint` on each `ViolationMapping` are forwarded
 *   verbatim per G-024 mitigation; engine v2 treats `'pending-tenant-bind'`
 *   as do-not-fire.
 *
 * @contract toCanonicalForm
 *
 * Purpose: trivial type-shape converter from builder `TemplateBuilderForm` to runtime
 * `InspectionForm`. See D-0.4.
 *
 * Fields read from input:
 * - _id, name, description, category, fields, layout, display,
 *   initialVisibility, rules, crossFormDependencies, version, updatedAt,
 *   formKind, moduleConfig.
 * - With `sourceFormat: 'mobile'`, formId/formName/formVersion and legacy
 *   fieldId/type and sectionId fields are normalized before conversion.
 *
 * Fields ignored (builder-only):
 * - templateType, templateTypeName, status, publishedAt/By, isFixedForm,
 *   fixedFormSourceId, derivedFrom, reusable, librarySourceId,
 *   librarySourceName, libraryDiverged, shared, sharing, visibility (template
 *   scope), aiMetadata, createdAt/By, deletedAt/By, _deleted.
 *
 * Rules:
 * - Idempotent. Returns a fresh object; never mutates the input.
 * - Missing optional fields collapse to `undefined`; required fields throw if
 *   absent (catches structurally-invalid input early in dev).
 * - Mobile input normalization preserves field metadata, violation mappings,
 *   N/A settings, initial visibility, and form rules.
 */

import type {
  InspectionForm,
  InspectionFormField,
  InspectionFormLayout,
  InspectionLayoutSection,
  InspectionRule,
  CrossFormDependency,
  FormDisplayConfig,
  FormKind,
  ModuleConfig,
} from '@firesquirrel/core-domain';

/**
 * Structural input shape accepted by the adapter. Matches the canonical
 * `InspectionForm` field set plus the builder-only superset; the adapter
 * walks only the canonical subset.
 * @contract CanonicalFormAdapterInput
 * Purpose: Describes canonical form adapter input at the shared type boundary.
 * Fields: _id, name, description, category, fields, layout, display, initialVisibility, rules, crossFormDependencies, version, updatedAt, formKind, moduleConfig
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export interface CanonicalFormAdapterInput {
  _id: string;
  name: string;
  description?: string;
  category?: string;

  fields: InspectionFormField[];
  layout: InspectionFormLayout;

  display?: FormDisplayConfig;
  initialVisibility?: 'shown' | 'hidden';

  rules?: InspectionRule[];
  crossFormDependencies?: CrossFormDependency[];

  version: number;
  updatedAt: string;

  formKind?: FormKind;
  moduleConfig?: ModuleConfig;

  // Any number of extra builder-only properties may be present. We accept and
  // ignore them.
  [key: string]: unknown;
}

/**
 * @contract MobileFormAdapterInput
 * Purpose: Structural shape for frozen mobile snapshots and legacy live forms.
 * Fields: Mobile snapshot identifiers, flat or nested fields, layout, rules,
 * version, and display/module metadata.
 * Rules: Used only with toCanonicalForm's sourceFormat='mobile' option.
 * Sync: Read-only input; no persistence is performed.
 */
export interface MobileFormAdapterInput {
  _id?: string;
  formId?: string;
  name?: string;
  formName?: string;
  description?: string;
  category?: string;
  fields?: Array<Record<string, unknown>>;
  sections?: Array<Record<string, unknown>>;
  layout?: { sections?: Array<Record<string, unknown>>; [key: string]: unknown };
  rules?: unknown[];
  crossFormDependencies?: CrossFormDependency[];
  version?: number;
  formVersion?: number;
  updatedAt?: string;
  initialVisibility?: 'shown' | 'hidden';
  display?: FormDisplayConfig;
  formKind?: FormKind;
  moduleConfig?: ModuleConfig;
  [key: string]: unknown;
}

/**
 * @contract CanonicalFormAdapterOptions
 * Purpose: Selects optional source-shape normalization before canonicalization.
 * Fields: sourceFormat ('canonical' or 'mobile').
 * Rules: Omitted sourceFormat preserves the existing canonical adapter behavior.
 * Sync: Conversion is pure and does not affect sync state.
 */
export interface CanonicalFormAdapterOptions {
  sourceFormat?: 'canonical' | 'mobile';
}

function normalizeMobileFieldType(value: unknown): InspectionFormField['type'] {
  switch (value) {
    case 'inspector':
    case 'inspectors':
      return 'inspectors';
    case 'notes':
      return 'textarea';
    case 'heading':
      return 'section';
    case 'divider':
      return 'instructions';
    case 'lookup':
      return 'dropdown';
    case 'toggle':
    case 'text':
    case 'textarea':
    case 'number':
    case 'measurement':
    case 'dropdown':
    case 'checkboxGroup':
    case 'radioGroup':
    case 'camera':
    case 'date':
    case 'dateTime':
    case 'photo':
    case 'signature':
    case 'location':
    case 'section':
    case 'instructions':
    case 'repeatableList':
    case 'dataBound':
      return value;
    default:
      return 'text';
  }
}

function normalizeMobileForm(input: MobileFormAdapterInput): CanonicalFormAdapterInput {
  const formId = String(input._id ?? input.formId ?? '');
  const sourceFields = input.fields?.length
    ? input.fields
    : (input.sections ?? []).flatMap((section) =>
        ((section.fields as Array<Record<string, unknown>> | undefined) ?? []),
      );
  const fields = sourceFields.map((field) => {
    const { fieldId, fieldType, ...rest } = field;
    const extraFields = [
      'placeholder',
      'readOnly',
      'columnSpan',
      'order',
      'options',
      'lookupType',
      'lookupScope',
      'dataSource',
      'boundField',
      'min',
      'max',
      'step',
      'trueLabel',
      'falseLabel',
      'triggerViolation',
      'violationCodeId',
      'violationTriggerValue',
      'rules',
      'sectionId',
    ];
    const extras = { ...((field.extras as Record<string, unknown> | undefined) ?? {}) };
    for (const key of extraFields) {
      if (key in field) extras[key] = field[key];
    }
    return {
      ...rest,
      id: String(field.id ?? fieldId ?? ''),
      type: normalizeMobileFieldType(field.type ?? fieldType),
      extras,
    } as InspectionFormField;
  });
  const sourceSections = input.layout?.sections ?? input.sections ?? [];
  let sections: InspectionLayoutSection[] = sourceSections.map((section, index) => {
    const sectionId = String(section.id ?? section.sectionId ?? `${formId}-sec-${index}`);
    const nestedFields = (section.fields as Array<Record<string, unknown>> | undefined) ?? [];
    return {
      id: sectionId,
      title: section.title as string | undefined,
      description: section.description as string | undefined,
      columns: (section.columns as 1 | 2 | 3 | undefined) ?? 1,
      order: (section.order as number | undefined) ?? index,
      fieldIds: (section.fieldIds as string[] | undefined)
        ?? nestedFields.map((field) => String(field.fieldId ?? field.id ?? '')),
      collapsible: section.collapsible as boolean | undefined,
      defaultCollapsed: section.defaultCollapsed as boolean | undefined,
      initialVisibility: section.initialVisibility as 'shown' | 'hidden' | undefined,
    };
  });
  sections = sections.map((section) => {
    if (section.fieldIds.length > 0) return section;
    const sectionFieldIds = fields
      .filter((field) => {
        const fieldRecord = field as unknown as Record<string, unknown>;
        const topLevelSectionId = fieldRecord.sectionId;
        const extrasSectionId = (fieldRecord.extras as Record<string, unknown> | undefined)?.sectionId;
        return String(topLevelSectionId ?? extrasSectionId ?? '') === section.id;
      })
      .map((field) => String(field.id ?? ''));
    return sectionFieldIds.length > 0 ? { ...section, fieldIds: sectionFieldIds } : section;
  });
  const assignedFieldIds = new Set(sections.flatMap((section) => section.fieldIds));
  const unassignedFieldIds = fields
    .map((field) => String(field.id ?? ''))
    .filter((fieldId) => !assignedFieldIds.has(fieldId));
  const firstPopulatedSectionIndex = sections.findIndex((section) => section.fieldIds.length > 0);
  if (unassignedFieldIds.length > 0 && firstPopulatedSectionIndex >= 0) {
    sections = sections.map((section, index) => (
      index === firstPopulatedSectionIndex
        ? { ...section, fieldIds: [...section.fieldIds, ...unassignedFieldIds] }
        : section
    ));
  }
  if (sections.length === 0 || sections.every((section) => section.fieldIds.length === 0)) {
    sections = [{
      id: `${formId}-sec-0`,
      title: input.name ?? input.formName,
      columns: 1,
      order: 0,
      fieldIds: fields.map((field) => String(field.id ?? '')),
    }];
  }

  return {
    _id: formId,
    name: String(input.name ?? input.formName ?? 'Form'),
    description: input.description,
    category: input.category,
    fields,
    layout: { columns: input.layout?.columns as 1 | 2 | 3 | undefined, sections },
    display: input.display,
    initialVisibility: input.initialVisibility,
    rules: input.rules as InspectionRule[] | undefined,
    crossFormDependencies: input.crossFormDependencies,
    version: input.version ?? input.formVersion ?? 1,
    updatedAt: input.updatedAt ?? '',
    formKind: input.formKind,
    moduleConfig: input.moduleConfig,
  };
}

/**
 * Convert a builder-shape form to a runtime-canonical `InspectionForm`.
 *
 * @throws Error if the input is missing one of the canonical required fields
 *         (`_id`, `name`, `fields`, `layout`, `version`, `updatedAt`).
 * @process toCanonicalForm
 * Steps: Normalize mobile field and section identifiers, assign ungrouped fields, then convert to canonical form.
 * Inputs: input, options
 * Outputs: InspectionForm
 * Failure Handling: Propagates failures through the declared return or exception contract.
 * Idempotency: REQUIRED — repeated calls with the same inputs produce the same result.
 * @errorHandling toCanonicalForm
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 */
export function toCanonicalForm(
  input: CanonicalFormAdapterInput | MobileFormAdapterInput,
  options: CanonicalFormAdapterOptions = {},
): InspectionForm {
  if (!input || typeof input !== 'object') {
    throw new Error('toCanonicalForm: input must be an object');
  }
  const normalized = options.sourceFormat === 'mobile'
    ? normalizeMobileForm(input as MobileFormAdapterInput)
    : input as CanonicalFormAdapterInput;
  for (const required of ['_id', 'name', 'fields', 'layout', 'version', 'updatedAt'] as const) {
    if ((normalized as Record<string, unknown>)[required] == null) {
      throw new Error(`toCanonicalForm: input is missing required field "${required}"`);
    }
  }

  // Defensive shallow clone of layout.sections to avoid surprising the caller
  // if they later mutate either side. Field array and rules array are also
  // shallow-cloned for the same reason.
  const layout: InspectionFormLayout = {
    columns: normalized.layout.columns,
    sections: (normalized.layout.sections ?? []).map((section: InspectionLayoutSection) => ({ ...section, fieldIds: [...section.fieldIds] })),
  };

  return {
    _id: normalized._id,
    name: normalized.name,
    description: normalized.description,
    category: normalized.category,
    fields: normalized.fields.map((field) => ({ ...field })),
    layout,
    display: normalized.display,
    initialVisibility: normalized.initialVisibility,
    rules: normalized.rules ? normalized.rules.map((rule) => ({ ...rule, then: [...rule.then] })) : undefined,
    crossFormDependencies: normalized.crossFormDependencies ? normalized.crossFormDependencies.map((dep) => ({ ...dep })) : undefined,
    version: normalized.version,
    updatedAt: normalized.updatedAt,
    formKind: normalized.formKind,
    moduleConfig: normalized.moduleConfig,
  };
}
